import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { GuildMemberResponse } from '@/lib/api';

interface TransferOwnershipCardProps {
  members: GuildMemberResponse[];
  /**
   * `members` is a page-capped list (useGuildMembers fetches 50 at a time), so
   * without these the picker silently omitted everyone past the first page.
   * Reused the hook's paging rather than the chat arche's `memberPicker` query
   * — that pattern is the same paged fetch, just under a different cache key,
   * so duplicating it here would buy nothing.
   */
  hasMore: boolean;
  onLoadMore: () => void;
  currentUserId: string | null;
  onTransfer: (userId: string) => Promise<unknown>;
}

/**
 * Hands the guild to another member. Owner-only and irreversible from the
 * transferring side, so it sits behind an explicit confirm naming the recipient.
 */
export function TransferOwnershipCard({
  members,
  hasMore,
  onLoadMore,
  currentUserId,
  onTransfer,
}: TransferOwnershipCardProps) {
  const [selected, setSelected] = useState<string>('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const candidates = members.filter((m) => m.userId !== currentUserId && m.role !== 'owner');
  const recipient = candidates.find((m) => m.userId === selected);

  const handleTransfer = async () => {
    if (!selected) return;
    setTransferring(true);
    setError(null);
    try {
      await onTransfer(selected);
      setConfirmOpen(false);
      setSelected('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not transfer ownership');
      // Close the confirm dialog so the inline error is actually visible —
      // while it is open, everything behind the overlay is aria-hidden.
      setConfirmOpen(false);
    } finally {
      setTransferring(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label htmlFor="transfer-owner">Transfer ownership</Label>
        <p className="text-sm text-muted-foreground">
          Hand this guild to another member. You stay in the guild as an admin, and only the new
          owner can transfer it back.
        </p>
      </div>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {candidates.length === 0 && !hasMore ? (
        <p className="text-sm text-muted-foreground">
          There’s no one else in this guild to transfer to yet.
        </p>
      ) : (
        <>
          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[12rem]">
              <Select value={selected} onValueChange={setSelected}>
                <SelectTrigger id="transfer-owner">
                  <SelectValue placeholder="Choose a member" />
                </SelectTrigger>
                <SelectContent>
                  {candidates.map((m) => (
                    <SelectItem key={m.userId} value={m.userId}>
                      {m.username || m.userId}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="destructive"
              disabled={!selected}
              onClick={() => setConfirmOpen(true)}
            >
              Transfer
            </Button>
          </div>
          {hasMore && (
            <Button type="button" variant="outline" size="sm" onClick={onLoadMore}>
              Load more members
            </Button>
          )}
        </>
      )}

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!transferring) setConfirmOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Make {recipient?.username || recipient?.userId} the owner?
            </AlertDialogTitle>
            <AlertDialogDescription>
              They gain full control of this guild, including the ability to remove you. You become
              an admin. This can only be undone by the new owner.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={transferring}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleTransfer();
              }}
              disabled={transferring}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {transferring ? 'Transferring…' : 'Transfer ownership'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
